import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AssignPermission, Complaint, ComplaintPosition, complaintPriorities, ComplaintPriorities, ComplaintsDocumentType, ComplaintsDocumentTypeDict, ComplaintsDocumentTypes, ComplaintSurveyStatus, ComplaintUserType, DocumentStatus, PersonsService } from '@icc/b2b/shared';
import { DialogService, InputComponent } from '@icc/ui';
import { ComplaintParty } from 'libs/b2b/shared/src/lib/models/complaint-party';
import { ComplaintUserPreviewDialogComponent } from '../complaint-user-preview-dialog/complaint-user-preview-dialog.component';
import { ConfigService, TranslateService } from '@icc/shared';
import { ToastService } from '@icc/shared/helpers';
import { ComplaintChangeUserDialogComponent } from '../complaint-change-user-dialog/complaint-change-user-dialog.component';
import { ComplaintService } from '../complaint.service';
import { A, V } from '@angular/cdk/keycodes';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, combineLatest } from 'rxjs';
import { Common, parseDate } from '@icc/shared/helpers';
import { NgxPermissionsService } from 'ngx-permissions';
import { ComplaintInfoComponent } from '../complaint-info/complaint-info.component';
import { ComplaintButton } from 'libs/b2b/shared/src/lib/models/complaint-workflow-rule';
import { Store } from '@ngrx/store';
import { changeDoc, SyncService } from '@icc/b2b/sales-document';

@Component({
    selector: 'icc-complaint-summary',
    templateUrl: './complaint-summary.component.html',
    styleUrls: ['./complaint-summary.component.scss'],
})
export class ComplaintSummaryComponent implements OnInit, OnChanges, OnDestroy {
    @Input() document!: Complaint;
    @Input() preview = false;
    @Input() positionsWorkflowsGroups: Map<number, ComplaintPosition[]> = new Map();
    @Input() positions: ComplaintPosition[] = [];
    @Input() selectedPositions: string[] = [];
    @Input() appointments: any[] = [];
    @Input() appointmentsAttachments: any[] = [];
    @Output() complaintTypeChange = new EventEmitter<ComplaintsDocumentType>();
    @Output() clearSelectedPositions = new EventEmitter<void>();

    hasAccessTo = {
        manageComplaint: false,
        appealToDistributor: false,
        appealToManufacturer: false,
        receivedFromManufacturer: false,
        receivedFromDistributor: false,
        partsSentAps: false,
        returnResignationAps: false,
        partsSentDistributor: false,
        returnResignationDistributor: false,
    };

    documentTypes = ComplaintsDocumentTypes;
    documentTypesDict = ComplaintsDocumentTypeDict;
    number = Number;

    documentPriorities = complaintPriorities;

    status: DocumentStatus | undefined;
    authorizedRetailerStatus: DocumentStatus | undefined;
    distributorStatus: DocumentStatus | undefined;
    manufacturerStatus: DocumentStatus | undefined;

    surveyStatus: ComplaintSurveyStatus | undefined;
    private loadedSurveyStatusDocumentUuid = '';
    private loadingSurveyStatusDocumentUuid = '';
    private loadingSurveyStatusPromise: Promise<void> | null = null;

    private subscriptions: Subscription[] = [];

    origin: 'list' | 'map' | 'appointment-list' | 'appointment-map' | null;

    constructor(
        private dialogService: DialogService,
        private configService: ConfigService,
        private complaintService: ComplaintService,
        private personsService: PersonsService,
        private router: Router,
        private route: ActivatedRoute,
        private permissionsService: NgxPermissionsService,
        private translateService: TranslateService,
        private toastService: ToastService,
        private store: Store,
        private syncService: SyncService,
    ) {
        this.origin = this.route.snapshot.queryParamMap.get('origin') as 'list' | 'map' | 'appointment-list' | 'appointment-map' | null;
        this.subscriptions.push(
            this.permissionsService.permissions$.subscribe(() => {
                this.checkAccess();
            }),
            this.syncService.complaintSurveyStatusChanged$.subscribe(({ documentId, status }) => {
                if (documentId === this.document?.uuid) {
                    this.surveyStatus = status;
                    this.loadedSurveyStatusDocumentUuid = documentId;
                    this.complaintService.setComplaintSurveyStatus(documentId, status);
                }
            }),
        );
    }

    async ngOnInit(): Promise<void> {
        await this.personsService.loaded;
        await this.complaintService.workflowsLoaded;
        this.status = this.complaintService.statuses.find((s) => +s.id === +this.document.statusId);
        this.authorizedRetailerStatus = this.document.authorizedRetailerCompanyId
            ? this.complaintService.statuses.find((s) => +s.id === +this.document.authorizedRetailerStatusId)
            : undefined;
        this.distributorStatus = this.complaintService.statuses.find((s) => +s.id === +this.document.distributorStatusId);
        this.manufacturerStatus = this.complaintService.statuses.find((s) => +s.id === +this.document.manufacturerStatusId);
        if (this.getUserType() === 'manufacturer') {
            this.loadSurveyStatus();
        }
    }

    async ngOnChanges(): Promise<void> {
        await this.personsService.loaded;
        this.status = this.complaintService.statuses.find((s) => +s.id === +this.document.statusId);
        this.authorizedRetailerStatus = this.document.authorizedRetailerCompanyId
            ? this.complaintService.statuses.find((s) => +s.id === +this.document.authorizedRetailerStatusId)
            : undefined;
        this.distributorStatus = this.complaintService.statuses.find((s) => +s.id === +this.document.distributorStatusId);
        this.manufacturerStatus = this.complaintService.statuses.find((s) => +s.id === +this.document.manufacturerStatusId);
        this.checkAccess();
        if (this.getUserType() === 'manufacturer') {
            this.loadSurveyStatus();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    async checkAccess() {
        this.hasAccessTo.manageComplaint = await this.permissionsService.hasPermission('complaints-manage') && ((this.document.assignedTo && this.document.assignedTo.startsWith(this.getUserType())) || await this.permissionsService.hasPermission('complaints-manage-manufacturer'));
    }

    openChangeUserDialog(userType: string) {
        const user = this.getUserByType(userType);

        this.dialogService
            .open(ComplaintChangeUserDialogComponent, {
                data: {
                    user,
                    userType,
                    companyId: this.configService.user?.user?.ActiveCompanyLicenseType === 'license'
                        ? userType === 'authorizedRetailer'
                            ? this.document.distributorCompanyId
                            : this.configService.user?.company?.id
                        : this.document.distributorCompanyId,
                    chooseContractorContractor:
                        this.configService.user?.user?.ActiveCompanyLicenseType === 'license' &&
                        userType === 'authorizedRetailer',
                    complaint: this.document,
                },
            })
            .afterClosed()
            .subscribe((data) => {
                if (data) {
                    this.complaintService.changeUser(this.document, data, userType);
                }
            });
    }

    getUserByType(userType: string): ComplaintParty | undefined {
        return this.complaintService.getUserByType(userType, this.document);
    }

    getUserType() {
        return this.complaintService.getUserType();
    }

    getPriority() {
        const rawPriority = this.complaintService.getPriority(this.document);
        if (rawPriority) {
            const priorityGroup = ComplaintPriorities[+rawPriority.slice(0, 1)];
            const priorityValue = rawPriority.slice(1);
            return this.translateService.instant(priorityGroup) + '-' + priorityValue;
        }
        return '';
    }

    getAssignedTo() {
        return this.complaintService.getAssignedTo(this.document, this.document.assignedTo, this.document.assignedCompanyId);
    }

    getAssignToValue() {
        if (this.document.assignedTo === 'distributor') {
            return 'distributor-' + this.document.assignedCompanyId;
        }
        return this.document.assignedTo;
    }

    getResponsibleUser() {
        return this.document?.[this.getUserType()]?.referenceName ?? '';
    }

    getAssignedUserName() {
        return this.complaintService.getAssignedToUserName(this.document, this.getUserType());
    }

    getAssignedUser() {
        return (
            this.personsService.getWorkerByUserId(
                this.document[
                    this.getUserType() + 'UserId' as
                        | 'authorizedRetailerUserId'
                        | 'distributorUserId'
                        | 'manufacturerUserId'
                ] ?? 0
            )?.person?.reference_name ?? ''
        );

    }

    findClosestAppointmentDate() {
        const now = new Date();
        now.setDate(now.getDate() - 1);
        const futureAppointmentsForDocument = this.appointments.filter(appointment =>
            !appointment.isDeleted &&
            new Date(appointment.date).getTime() >= now.getTime() &&
            (appointment.status !== 'canceled' && appointment.status !== 'completed') &&
            !appointment.servicemanSignatureImage
        );

        if (futureAppointmentsForDocument.length === 0) {
            return null;
        }

        const closestAppointment = futureAppointmentsForDocument.reduce((closest, current) => {
            const closestDateDiff = Math.abs(new Date(closest.date).getTime() - now.getTime());
            const currentDateDiff = Math.abs(new Date(current.date).getTime() - now.getTime());
            return currentDateDiff < closestDateDiff ? current : closest;
        });

        return closestAppointment.date;
    }

    private async loadSurveyStatus(force = false): Promise<void> {
        const documentUuid = this.document?.uuid;
        if (!documentUuid) {
            return;
        }
        if (this.loadingSurveyStatusDocumentUuid === documentUuid && this.loadingSurveyStatusPromise) {
            return this.loadingSurveyStatusPromise;
        }
        if (!force && this.loadedSurveyStatusDocumentUuid === documentUuid) {
            return;
        }

        this.loadingSurveyStatusDocumentUuid = documentUuid;
        this.loadingSurveyStatusPromise = this.fetchSurveyStatus(documentUuid, force);
        return this.loadingSurveyStatusPromise;
    }

    private async fetchSurveyStatus(documentUuid: string, force = false): Promise<void> {
        try {
            this.surveyStatus = await this.complaintService.getComplaintSurveyStatus(documentUuid, force);
            this.loadedSurveyStatusDocumentUuid = documentUuid;
        } catch (error) {
            this.surveyStatus = { status: 'not_sent' };
        } finally {
            if (this.loadingSurveyStatusDocumentUuid === documentUuid) {
                this.loadingSurveyStatusDocumentUuid = '';
                this.loadingSurveyStatusPromise = null;
            }
        }
    }

    openSurveyPreview(): void {
        if (this.surveyStatus?.submissionUuid) {
            this.router.navigate(['/trade/complaint-survey', this.surveyStatus.submissionUuid, 'preview']);
        }
    }
}
